<!--
  File name: AnalyticsForm.vue
  Program Description:
    - This component renders the action list made by the user.
    - It allows the user to evaluate their actions throughout the selected semester

  Includes:
    - Ratings
-->
<template>
  <v-container flex>
    <template v-if="loading">
      <v-row>
        <v-col>
          <v-skeleton-loader
            class="mx-auto"
            type="text"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-skeleton-loader
            class="mx-auto"
            type="table"
          />
        </v-col>
      </v-row>
    </template>
    <template v-else-if="success && !loading">
      <v-row>
        <v-col>
          <h1>
            {{ formTitle }}
            <v-chip
              v-if="formStatus"
              :class="chipProperties(formStatus)"
            >
              {{ formStatus }}
            </v-chip>
          </h1>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-stepper
            v-model="tab"
            editable
            color="#d6b315"
            elevation="0"
            class="pb-0 transparent-bg"
          >
            <v-stepper-header
              class="ma-0"
            >
              <template
                v-for="(item, index) in apiData"
              >
                <v-stepper-step
                  :key="item.tabName"
                  :step="index+1"
                  editable
                  @click="filter.search = null"
                >
                  <span
                    class="headerLabel"
                  >
                    {{ item.tabName }}
                  </span>
                </v-stepper-step>
                <v-divider
                  v-if="index !== (apiData.length - 1 )"
                  :key="`divider-${index}`"
                  class="mx-1"
                />
              </template>
            </v-stepper-header>
          </v-stepper>
        </v-col>
      </v-row>
      <v-row>
        <v-spacer />
        <v-col class="d-flex justify-end">
          <template v-if="canUpdateForm">
            <v-btn
              v-if="readOnly"
              id="update-documents-btn"
              color="primary"
              type="button"
              class="mr-2"
              @click="$router.push({
                name: 'AnalyticsForm',
                params: { operation: 'update' },
                query: {user: $route.query.user}
              })"
            >
              <v-icon class="mr-2">
                mdi-pencil
              </v-icon>
              Update
            </v-btn>
            <v-btn
              v-if="readOnly"
              id="approve-documents-btn"
              color="success"
              type="button"
              :disabled="!isLastTab"
              @click="validate('approve')"
            >
              <v-icon class="mr-2">
                mdi-check
              </v-icon>
              Approve
            </v-btn>
            <v-btn
              v-if="!readOnly && operation === 'update' && isDpcr"
              id="approve-documents-btn"
              color="primary"
              type="button"
              :disabled="!isLastTab"
              @click="validate('update')"
            >
              Save
            </v-btn>
          </template>
          <template v-else>
            <v-btn
              v-if="!readOnly && operation === 'create' && !isDpcr"
              id="submit-documents-btn"
              color="primary"
              type="button"
              :disabled="!isLastTab"
              @click="validate('submit')"
            >
              Submit IPCR
            </v-btn>
          </template>
        </v-col>
      </v-row>
      <v-row
        align-self="start"
        justify="center"
      >
        <v-col class="mb-0 pb-0">
          <v-card
            elevation="0"
            tile
            outlined
          >
            <v-card-title class="pt-2 pb-2">
              <template v-if="readOnly">
                <v-btn
                  id="back-btn"
                  text
                  color="grey darken-1"
                  @click="$router.push({ name: 'Analytics', query:{view:true, year: selectedYear, sem: selectedSem, user: $route.query.user }})"
                >
                  <v-icon>mdi-arrow-left-thin</v-icon>
                  {{ isDpcr ? 'Back' : 'Back to IPCR Lists' }}
                </v-btn>
              </template>
              <template v-else>
                <v-btn
                  id="cancel-edit-btn"
                  color="red darken-1"
                  text
                  @click="discardChanges"
                >
                  Cancel
                </v-btn>
              </template>
              <v-spacer />
              <v-menu
                v-model="filter.menu"
                :close-on-content-click="false"
                :nudge-width="200"
                max-width="300"
                min-width="300"
                offset-y
              >
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-show="!isLastTab"
                    id="filter-btn"
                    text
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon large>
                      mdi-filter-variant
                    </v-icon>
                  </v-btn>
                </template>
                <v-card class="pa-6">
                  <h2>Filters</h2>
                  <v-checkbox
                    v-model="filter.search"
                    label="Timely"
                    value="Timely"
                    hide-details
                  />
                  <v-checkbox
                    v-model="filter.search"
                    label="Delayed"
                    value="Delayed"
                    hide-details
                  />
                </v-card>
              </v-menu>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="isLastTab">
        <v-col class="my-0 py-0">
          <v-data-table
            :headers="summaryHeaders"
            :items="summaryOfEvaluation"
            style="border: thin solid rgba(0, 0, 0, 0.12)"
            hide-default-footer
            disable-sort
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="mt-0 pt-0">
          <v-tabs-items v-model="tabMinus">
            <v-tab-item
              v-for="tabItem, index in apiData"
              :key="tabItem.tabName"
            >
              <v-row>
                <v-col>
                  <v-card
                    elevation="0"
                    tile
                    outlined
                    :class="tabItem.tabName === 'Rating' ? 'py-6 px-12' : ''"
                  >
                    <template v-if="tabItem.tabName === 'Rating'">
                      <h1
                        style="color:#272A68"
                        class="mb-3"
                      >
                        Ratings
                      </h1>
                      <div
                        v-for="docTab, i in documentTabs"
                        :key="i"
                        class="mb-5"
                      >
                        <v-row>
                          <v-col cols="8">
                            <h2>{{ docTab }}</h2>
                          </v-col>
                          <v-col
                            cols="2"
                            class="d-flex"
                          >
                            <p class="ma-0 d-flex align-center">
                              Average:
                            </p>
                            <v-text-field
                              class="ml-2 centered"
                              :value="averageRating(docTab)"
                              dense
                              outlined
                              disabled
                              hide-details
                            />
                          </v-col>
                        </v-row>
                        <Ratings
                          v-if="role !== 'Assignor'"
                          v-model="$data[`${docTab.toLowerCase()}Rating`].quality"
                          :disabled="readOnly"
                          :subtitle="`How satisfied are you by the quality of ${docTab}?`"
                          title="1. Quality"
                          :error-message="{ show: $v[`${docTab.toLowerCase()}Rating`].quality.$error, value: 'Quality is required.'}"
                        />
                        <Ratings
                          v-model="$data[`${docTab.toLowerCase()}Rating`].efficiency"
                          :disabled="readOnly"
                          :title="`${role === 'Assignor' ? '1.' : '2.'} Efficiency`"
                          :subtitle="`How satisfied are you by the efficiency of ${docTab}?`"
                          :error-message="{ show: $v[`${docTab.toLowerCase()}Rating`].efficiency.$error, value: 'Efficiency is required.'}"
                        />
                        <Ratings
                          v-model="$data[`${docTab.toLowerCase()}Rating`].timeliness"
                          :disabled="readOnly"
                          :subtitle="`How satisfied are you by the timeliness of ${docTab}?`"
                          :title="`${role === 'Assignor' ? '2.' : '3.'} Timeliness`"
                          :error-message="{ show: $v[`${docTab.toLowerCase()}Rating`].timeliness.$error, value: 'Timeliness is required.'}"
                        />
                        <v-divider v-if="(documentTabs.length - 1) !== i" />
                      </div>
                    </template>
                    <template v-else>
                      <v-data-table
                        :headers="tabItem.headers"
                        :items="tabItem.tableData"
                        :custom-filter="filterTable"
                        :items-per-page="-1"
                        :search="filter.search"
                        class="elevation-1"
                        hide-default-footer
                        disable-sort
                      >
                        <template v-slot:[`item.timeliness`]="props">
                          <v-autocomplete
                            v-model="props.item.timeliness"
                            :error-messages="analyticsError('timeliness', 'Timeliness', index, props.index)"
                            :items="['Timely', 'Delayed']"
                            :disabled="readOnly"
                            class="pt-6"
                            outlined
                            dense
                            @blur="$v.apiData.$each[index].tableData.$each[props.index].timeliness.$touch()"
                            @input="$v.apiData.$each[index].tableData.$each[props.index].timeliness.$touch()"
                          />
                        </template>
                        <template v-slot:[`item.remarks`]="{ item }">
                          <v-textarea
                            v-model="item.remarks"
                            :disabled="readOnly"
                            :readonly="readOnly"
                            class="pt-6 cursor"
                            rows="1"
                            outlined
                            dense
                          />
                        </template>
                        <template v-slot:[`item.assignedDate`]="{ item }">
                          {{ toDateString(item.assignedDate) }}
                        </template>
                        <template v-slot:[`item.evaluationDate`]="{ item }">
                          {{ toDateString(item.evaluationDate) }}
                        </template>
                        <template v-slot:[`item.finalReviewDate`]="{ item }">
                          {{ toDateString(item.finalReviewDate) }}
                        </template>
                        <template v-slot:[`item.notificationDate`]="{ item }">
                          {{ toDateString(item.notificationDate) }}
                        </template>
                        <template v-slot:[`item.initialReviewDate`]="{ item }">
                          {{ toDateString(item.initialReviewDate) }}
                        </template>
                      </v-data-table>
                    </template>
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="1">
          <v-btn
            v-show="!isStartTab"
            id="back-btn"
            text
            @click="tab = tab - 1"
          >
            <v-icon class="mr-1">
              mdi-arrow-left
            </v-icon> Back
          </v-btn>
        </v-col>
        <v-spacer />
        <v-col
          cols="2"
          class="d-flex justify-end"
        >
          <v-btn
            v-show="!isLastTab"
            id="continue-btn"
            color="#d6b315"
            class="white--text"
            @click="tab = tab + 1"
          >
            Continue
            <v-icon class="mr-1">
              mdi-arrow-right
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <v-card class="pa-12 mt-4">
        <NoDataYet
          :image="implem"
          message="No data"
        />
      </v-card>
    </template>
    <v-btn
      v-if="showFab"
      fab
      fixed
      bottom
      right
      class="primary"
      @click="scrollToTop()"
    >
      <v-icon
        color="#FFFFFF"
      >
        mdi-arrow-up
      </v-icon>
    </v-btn>
  </v-container>
</template>

<script>

import crud from '@/utils/rest/crud'
import implem from '@/assets/implem.png'
import Ratings from '@/components/Ratings'
import { validationMixin } from 'vuelidate'
import { custom } from '@/utils/validations'
import validationErrors from '@/utils/validations/errors'
import { toDateString, capitalize } from '@/utils/helpers'
export default ({
  name: 'Analytics',
  components: {
    Ratings
  },
  mixins: [
    validationMixin
  ],
  data: () => ({
    tab: 1,
    showFab: false,
    discard: false,
    loading: true,
    success: true,
    filter: {
      menu: false,
      search: ''
    },
    summaryHeaders: [
      {
        text: 'DBN Actual Accomplishments',
        value: 'name',
        cellClass: 'grey--text',
        class: 'font-weight-black text-subtitle-1 black--text'
      },
      {
        text: 'Timely',
        value: 'timeliness',
        align: 'center',
        cellClass: 'font-weight-black text-subtitle-1',
        class: 'font-weight-black text-subtitle-1 black--text'
      },
      {
        text: 'Delayed',
        value: 'delayed',
        align: 'center',
        cellClass: 'font-weight-black text-subtitle-1',
        class: 'font-weight-black text-subtitle-1 black--text'
      },
      {
        text: 'Total',
        value: 'total',
        align: 'center',
        cellClass: 'font-weight-black text-subtitle-1',
        class: 'font-weight-black text-subtitle-1 black--text'
      }
    ],
    status: '',
    apiData: [],
    fullName: '',
    year: '',
    sem: '',
    pdbnRating: {
      quality: 0,
      efficiency: 0,
      timeliness: 0
    },
    fbnerRating: {
      quality: 0,
      efficiency: 0,
      timeliness: 0
    },
    pbnefRating: {
      quality: 0,
      efficiency: 0,
      timeliness: 0
    }
  }),
  computed: {
    ...validationErrors,
    tabs () {
      if (this.apiData && this.apiData.length) return this.apiData.map(item => item.tabName)
      return []
    },
    documentTabs () {
      if (!this.apiData.length) return []
      const tabs = [...this.apiData].filter(item => item.tableData && item.tableData.length)
      return tabs.map(item => item.tabName.substring(0, 5).trim())
    },
    tabMinus () {
      return this.tab - 1
    },
    isLastTab () {
      return this.tab === this.tabs.length
    },
    isStartTab () {
      return this.tab === 1
    },
    currentTab () {
      return this.tabs[this.tabMinus]
    },
    inRatingTab () {
      return this.currentTab === 'Rating'
    },
    userRole () {
      return this.$s && this.$s.userRole
    },
    userRolesHave () {
      return roles => Array.isArray(roles) && roles.some(role => this.userRole.includes(role))
    },
    canUpdateForm () {
      return this.isDpcr && (this.formStatus === 'Evaluated' || this.formStatus === 'Updated')
    },
    currentSem () {
      return (new Date()).getMonth() <= 5 ? '1st' : '2nd'
    },
    currentYear () {
      return (new Date()).getFullYear()
    },
    selectedSem () {
      if (this.operation === 'view' && this.sem) return this.sem
      return this.$route.params.semester
    },
    selectedYear () {
      if (this.operation === 'view' && this.year) return this.year
      return this.$route.params.year
    },
    toDateString () {
      return date => toDateString(date)
    },
    implem () {
      return implem
    },
    summaryOfEvaluation () {
      if (!this.apiData.length) return {}
      const summary = []
      this.apiData.forEach(item => {
        if (item.tabName === 'Rating') return
        const total = item.tableData.length
        const timeliness = item.tableData.filter(item => item.timeliness === 'Timely').length
        const delayed = item.tableData.filter(item => item.timeliness === 'Delayed').length
        summary.push({
          name: `${item.tabName.substring(0, 5).trim()} ${this.action}`,
          timeliness,
          delayed,
          total
        })
      })
      return summary
    },
    averageRating () {
      return docType => {
        const rating = this.$data[`${docType.toLowerCase()}Rating`]
        const values = Object.values(rating)
        const sum = values.reduce((x, y) => x + y, 0)
        const divisor = this.role === 'Assignor' ? 2 : 3
        return (sum / divisor).toFixed(2)
      }
    },
    isDpcr () {
      return this.$route.params.dataTab === 'dpcr' && this.userRolesHave(['divisionChief'])
    },
    readOnly () {
      return this.operation === 'view' || this.operation === 'defaultView'
    },
    schema () {
      return {
        entry: this.apiData,
        status: 'Submitted',
        sem: this.selectedSem,
        ratingType: this.role,
        year: this.selectedYear,
        pbnefRating: this.pbnefRating,
        fbnerRating: this.fbnerRating,
        pdbnfRating: this.pdbnRating,
        actionType: this.$route.params.role
      }
    },
    filterTable () {
      return (value, search) => value === search
    },
    formValidation () {
      const rating = custom.ratings(this)
      const validations = {}
      this.documentTabs.forEach(docName => { validations[`${docName.toLowerCase()}Rating`] = rating })
      return validations
    },
    formTitle () {
      const semYear = `(${this.selectedYear} - ${this.selectedSem} Semester)`
      if (this.isDpcr) return `IPCR - ${this.fullName} ${semYear}`
      return `IPCR for ${this.role} ${semYear}`
    },
    formStatus () {
      if (this.status === 'Submitted') return 'Evaluated'
      if (this.status === 'Approved' || this.status === 'Updated') return this.status
      const canSubmitYear = parseInt(this.currentYear) - 1
      const Ongoing = (parseInt(this.selectedYear) === this.currentYear) && (this.selectedSem === this.currentSem)
      if (Ongoing) return 'Ongoing Semester'
      const canSubmitSem = this.currentSem === '1st' ? '2nd' : '1st'
      const Late = (parseInt(this.selectedYear) < canSubmitYear) || (parseInt(this.selectedYear) === canSubmitYear && this.selectedSem !== canSubmitSem)
      if (Late) return 'Late'
      return 'Pending'
    },
    operation () {
      const op = this.$route.params.operation
      if (this.userRolesHave(['divisionChief']) && op === 'update') return 'update'
      else if (this.userRolesHave(['reviewer', 'evaluator', 'assignor']) && op === 'create') return 'create'
      return op === 'view' ? 'view' : 'defaultView'
    },
    role () {
      const action = this.$route.params.role
      if (action === 'evaluate') return 'Evaluator'
      else if (action === 'initialReview') return 'Initial Reviewer'
      else if (action === 'finalReview') return 'Final Reviewer'
      else return 'Assignor'
    },
    action () {
      const action = this.$route.params.role
      if (action === 'evaluate') return 'Evaluated'
      else if (action === 'initialReview') return 'Initial Reviewed'
      else if (action === 'finalReview') return 'Final Reviewed'
      else return 'Assigned'
    },
    chipProperties () {
      return status => {
        let chip = ''
        if (status === 'Ongoing Semester') {
          chip = 'status_initial_order lighten-5 status_initial_order--text text--darken-1'
        } else if (status === 'Evaluated') {
          chip = 'green lighten-5 green--text text--darken-1'
        } else if (status === 'Approved') {
          chip = 'green darken-1 white--text'
        } else if (status === 'Late') {
          chip = 'status_closed darken-1 white--text text--lighten-3'
        } else if (status === 'Updated') {
          chip = 'status_pdbnf_review lighten-5 status_pdbnf_review--text text--darken-1'
        }
        return chip
      }
    }
  },
  mounted () {
    window.addEventListener('scroll', this.onScroll)
  },
  created () {
    this.getUserActions()
    window.addEventListener('beforeunload', this.beforeWindowUnload)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('beforeunload', this.beforeWindowUnload)
  },
  beforeRouteLeave (to, from, next) {
    if (!this.$s.userRole.includes('guest') && this.confirmStayInDirtyForm()) next(false)
    else next()
  },
  methods: {
    // show confirm window before leaving
    confirmLeave () {
      return window.confirm('Are you sure you want to leave? All unsaved data will be lost.')
    },
    // if not readOnly, trigger confirmLeave()
    confirmStayInDirtyForm () {
      if (!(this.readOnly || this.discard)) {
        return !this.confirmLeave()
      }
    },
    // prevent leaving form when user cancels confirmation
    beforeWindowUnload (e) {
      e.preventDefault()
      e.returnValue = ''
    },
    discardChanges () {
      this.$store.dispatch('showDialog', {
        title: 'Discard Changes',
        message: 'Changes will not be saved. Are you sure you want to discard?',
        buttonLabel: 'Discard',
        buttonColor: 'error',
        callback: () => {
          this.discard = true
          if (this.isDpcr) {
            this.$v.$reset()
            this.$router.go(-1)
            this.discard = false
            this.getUserActions()
          } else {
            this.$router.push({
              name: 'Analytics',
              query: {
                view: true,
                year: this.selectedYear,
                sem: this.selectedSem,
                user: this.$route.query.user
              }
            })
          }
        }
      })
    },
    validate (action) {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$store.dispatch('showAlert', {
          message: 'Please validate all the required fields.',
          type: 'error'
        })
      } else {
        const extMessage = action === 'submit' ? 'your IPCR evaluation' : `the IPCR of ${this.fullName}`
        this.$store.dispatch('showDialog', {
          title: `${capitalize(action)} IPCR`,
          message: `Are you sure you want to ${action} ${extMessage}?`,
          buttonLabel: 'Submit',
          buttonColor: 'success',
          callback: () => this.submitForm(action)
        })
      }
    },
    async submitForm (action) {
      this.loading = true
      let response = {}
      let message = ''
      if (action === 'submit') {
        response = await crud('analytics').create(this.schema)
        message = `You have successfully evaluated your IPCR for ${this.selectedSem} semester of ${this.selectedYear}`
      } else if (action === 'update') {
        response = await crud('analytics').update(this.$route.params.id, this.schema)
        message = `You have successfully updated the IPCR of ${this.fullName}`
      } else {
        response = await crud('analytics/approve').update(this.$route.params.id)
        message = `You have successfully approved the IPCR of ${this.fullName} for ${this.selectedYear} - ${this.selectedSem} Semester`
      }
      const jData = await response.json()
      this.$store.dispatch('showAlert', {
        message: jData.error || message,
        type: response.ok ? 'success' : 'error'
      })
      this.loading = false
      if (!response.ok) return
      action !== 'approve' && this.$router.replace({ params: { operation: 'view' }, query: { user: this.$route.query.user } })
      this.status = jData.status
      this.tab = 1
      action !== 'submit' && this.getUserActions()
      this.discard = false
    },
    async getUserActions () {
      this.loading = true
      let response = {}
      const id = this.$route.params.id
      const role = this.$route.params.role
      const year = this.$route.params.year
      const semester = this.$route.params.semester
      const canViewByUserId = (this.operation === 'create' && !this.isDpcr) || this.operation === 'defaultView'
      if (canViewByUserId) response = await crud(`actionOwnership/${role}/${id}/${year}/${semester}`).list()
      else if (this.operation === 'view' || this.operation === 'update') response = await crud(`analytics/${id}`).list()
      if (response.ok) {
        this.sem = response.sem
        this.year = response.year
        this.status = response.status
        this.apiData = response.entry
        this.fullName = response.fullName
        this.apiData.push({ tabName: 'Rating' })
        response.pdbnfRating && (this.pdbnRating = response.pdbnfRating)
        response.pbnefRating && (this.pbnefRating = response.pbnefRating)
        response.fbnerRating && (this.fbnerRating = response.fbnerRating)
      } else {
        this.success = false
      }
      this.loading = false
    },
    scrollToTop () {
      this.$vuetify.goTo(0, 0)
    },
    onScroll () {
      this.showFab = window.top.scrollY > 200
    }
  },
  validations () {
    return {
      apiData: {
        $each: {
          tableData: custom.tableData
        }
      },
      ...this.formValidation
    }
  }
})
</script>

<style scoped>
  .transparent-bg {
    background-color: rgba(0,0,0,0);
  }
  .centered >>> input {
    text-align: center;
    font-weight: bold;
  }
  .cursor >>>  textarea{
    overflow-y: hidden !important;
    cursor: default !important
  }
</style>
